import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search, Download, MoreHorizontal, UserX, UserCheck, Trash2, Loader2,
  Users, ShieldCheck, Building2, GraduationCap, Plus, Upload, Copy, KeyRound,
  ChevronDown, ChevronRight, Home, Mail, Ban, ArrowUpDown, Clock, CalendarDays,
  Timer,
  UserCog,
} from 'lucide-react';
import { ExtraTimeDialog } from '@/components/ExtraTimeDialog';
import { ChangeRoleDialog } from '@/components/ChangeRoleDialog';
import { InlineEdit } from '@/components/InlineEdit';

const TABS = [
  { id: 'all', label: 'All Users', icon: Users },
  { id: 'organisations', label: 'Organisations', icon: Building2 },
  { id: 'individuals', label: 'Individuals', icon: GraduationCap },
  { id: 'residents', label: 'Residents', icon: Home },
  { id: 'pending', label: 'Pending Invites', icon: Clock },
];

const ROLE_MAP = {
  super_admin: { color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldCheck, label: 'Super Admin' },
  content_manager: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: GraduationCap, label: 'Content Manager' },
  org_admin: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Building2, label: 'Org Admin' },
  learner: { color: 'bg-teal-100 text-teal-700 border-teal-200', icon: Users, label: 'Learner' },
};

const RoleBadge = ({ role }) => {
  const cfg = ROLE_MAP[role] || ROLE_MAP.learner;
  const Icon = cfg.icon;
  return <Badge className={`${cfg.color} text-[10px] gap-1 border`}><Icon className="h-3 w-3" /> {cfg.label}</Badge>;
};

const StatusBadge = ({ status }) => {
  if (status === 'suspended') return <Badge className="bg-red-50 text-red-600 border border-red-200 text-[10px]">Suspended</Badge>;
  if (status === 'pending') return <Badge className="bg-amber-50 text-amber-600 border border-amber-200 text-[10px]">Pending</Badge>;
  return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Active</Badge>;
};

// ── Sortable Table Header ──
const SortHeader = ({ label, field, sortBy, sortDir, onSort }) => (
  <th
    className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer hover:text-[#20acf6] select-none group"
    onClick={() => onSort(field)}
  >
    <span className="flex items-center gap-1">
      {label}
      <ArrowUpDown className={`h-3 w-3 transition-opacity ${sortBy === field ? 'opacity-100 text-[#20acf6]' : 'opacity-0 group-hover:opacity-50'}`} />
    </span>
  </th>
);

// ── User Row ──
const UserRow = ({ u, selected, onSelect, onAction, onRename }) => (
  <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${u.id}`}>
    <td className="px-4 py-3 w-10">
      <Checkbox checked={selected} onCheckedChange={() => onSelect(u.id)} data-testid={`select-user-${u.id}`} />
    </td>
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-[#20acf6]/10 rounded-full flex items-center justify-center text-[#20acf6] text-xs font-semibold shrink-0">
          {u.first_name?.[0]}{u.last_name?.[0]}
        </div>
        <div className="min-w-0">
          <InlineEdit value={`${u.first_name || ''} ${u.last_name || ''}`.trim()} onSave={(v) => onRename(u, v)} testId={`inline-name-${u.id}`} className="font-medium text-slate-800 max-w-[220px]" />
          <span className="text-xs text-slate-400 block truncate md:hidden">{u.email}</span>
        </div>
      </div>
    </td>
    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{u.email}</td>
    <td className="px-4 py-3 hidden sm:table-cell"><RoleBadge role={u.role} /></td>
    <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={u.status} /></td>
    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell text-xs">{u.created_at?.slice(0, 10)}</td>
    <td className="px-4 py-3 text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.id}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {u.status === 'suspended' ? (
            <DropdownMenuItem onClick={() => onAction('activate', u)}>
              <UserCheck className="h-4 w-4 mr-2" /> Activate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => onAction('suspend', u)}>
              <UserX className="h-4 w-4 mr-2" /> Suspend
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onAction('change_role', u)} data-testid={`user-change-role-${u.id}`}>
            <UserCog className="h-4 w-4 mr-2" /> Change role…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAction('extra_time', u)} data-testid={`user-extra-time-${u.id}`}>
            <Timer className="h-4 w-4 mr-2" /> Extra time…
          </DropdownMenuItem>
          <DropdownMenuItem className="text-red-600" onClick={() => onAction('delete', u)}>
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </td>
  </tr>
);

// ── Organisation Expandable Row ──
const OrgRow = ({ org, members, expanded, onToggle, selectedIds, onSelect, onAction }) => (
  <>
    <tr
      className="border-b border-slate-100 hover:bg-blue-50/30 cursor-pointer transition-colors"
      onClick={onToggle}
      data-testid={`org-row-${org.id}`}
    >
      <td className="px-4 py-3 w-10">
        {expanded ? <ChevronDown className="h-4 w-4 text-[#20acf6]" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </td>
      <td className="px-4 py-3" colSpan={2}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
            <Building2 className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <span className="font-semibold text-[#2a3e5d]">{org.name}</span>
            <span className="text-xs text-slate-400 ml-2">{members.length} user{members.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <Badge className="bg-blue-50 text-blue-600 border border-blue-200 text-[10px]">
          {org.subscription_tier || 'No Plan'}
        </Badge>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={org.status} /></td>
      <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{org.created_at?.slice(0, 10)}</td>
      <td className="px-4 py-3" />
    </tr>
    {expanded && members.map(u => (
      <tr key={u.id} className="border-b border-slate-50 bg-blue-50/20" data-testid={`org-member-${u.id}`}>
        <td className="px-4 py-2.5 w-10 pl-8">
          <Checkbox checked={selectedIds.has(u.id)} onCheckedChange={() => onSelect(u.id)} />
        </td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2.5 pl-4">
            <div className="w-7 h-7 bg-[#20acf6]/10 rounded-full flex items-center justify-center text-[#20acf6] text-[10px] font-semibold">
              {u.first_name?.[0]}{u.last_name?.[0]}
            </div>
            <div>
              <span className="font-medium text-slate-700 text-sm">{u.first_name} {u.last_name}</span>
              {u.team_role && <span className="text-[10px] text-slate-400 ml-1.5">({u.team_role})</span>}
            </div>
          </div>
        </td>
        <td className="px-4 py-2.5 text-slate-500 text-sm hidden md:table-cell">{u.email}</td>
        <td className="px-4 py-2.5 hidden sm:table-cell"><RoleBadge role={u.role} /></td>
        <td className="px-4 py-2.5 hidden sm:table-cell"><StatusBadge status={u.status} /></td>
        <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-400">{u.created_at?.slice(0, 10)}</td>
        <td className="px-4 py-2.5 text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {u.status === 'suspended' ? (
                <DropdownMenuItem onClick={() => onAction('activate', u)}><UserCheck className="h-4 w-4 mr-2" /> Activate</DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onAction('suspend', u)}><UserX className="h-4 w-4 mr-2" /> Suspend</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onAction('change_role', u)} data-testid={`user-change-role-${u.id}`}><UserCog className="h-4 w-4 mr-2" /> Change role…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction('extra_time', u)} data-testid={`user-extra-time-${u.id}`}><Timer className="h-4 w-4 mr-2" /> Extra time…</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600" onClick={() => onAction('delete', u)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
    ))}
  </>
);

// ═══════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════
const AdminUsersPage = () => {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Selection & bulk
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: '', first_name: '', last_name: '', password: '', role: 'learner', organisation_id: '', status: 'active', autoPassword: true });
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('add') !== '1') return;
    const role = searchParams.get('role');
    if (role && ROLE_MAP[role]) setAddUserForm(f => ({ ...f, role }));
    setShowAddUser(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);

  // Org expansion
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes, invitesRes] = await Promise.all([
        api.get('/users?limit=500').catch(() => ({ data: [] })),
        api.get('/organisations').catch(() => ({ data: [] })),
        api.get('/organisations/team/invitations').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data || []);
      setOrganisations(orgsRes.data || []);
      setInvitations(invitesRes.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived data ──
  const orgMap = useMemo(() => {
    const m = {};
    organisations.forEach(o => { m[o.id] = o; });
    return m;
  }, [organisations]);

  const orgNameById = (id) => orgMap[id]?.name || orgMap[id]?.company_name || '';

  const dateCutoff = useMemo(() => {
    if (dateFilter === 'all') return null;
    const d = new Date();
    if (dateFilter === '7') d.setDate(d.getDate() - 7);
    else if (dateFilter === '30') d.setDate(d.getDate() - 30);
    else if (dateFilter === '90') d.setDate(d.getDate() - 90);
    return d.toISOString();
  }, [dateFilter]);

  // ── Filtering ──
  const filteredUsers = useMemo(() => {
    let list = [...users];

    // Tab filtering
    if (activeTab === 'individuals') list = list.filter(u => !u.organisation_id && !u.is_resident);
    else if (activeTab === 'residents') list = list.filter(u => u.is_resident);
    else if (activeTab === 'organisations') return list; // handled separately

    // Filters
    if (roleFilter !== 'all') list = list.filter(u => u.role === roleFilter);
    if (statusFilter !== 'all') list = list.filter(u => u.status === statusFilter);
    if (orgFilter !== 'all') list = list.filter(u => u.organisation_id === orgFilter);
    if (dateCutoff) list = list.filter(u => (u.created_at || '') >= dateCutoff);

    // Search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        u.email?.toLowerCase().includes(q) ||
        u.first_name?.toLowerCase().includes(q) ||
        u.last_name?.toLowerCase().includes(q) ||
        orgNameById(u.organisation_id)?.toLowerCase().includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'name': va = `${a.first_name} ${a.last_name}`.toLowerCase(); vb = `${b.first_name} ${b.last_name}`.toLowerCase(); break;
        case 'email': va = a.email || ''; vb = b.email || ''; break;
        case 'organisation': va = orgNameById(a.organisation_id); vb = orgNameById(b.organisation_id); break;
        case 'last_login': va = a.last_login || ''; vb = b.last_login || ''; break;
        default: va = a.created_at || ''; vb = b.created_at || '';
      }
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [users, activeTab, roleFilter, statusFilter, orgFilter, dateCutoff, search, sortBy, sortDir]);

  // ── Org tab data ──
  const orgGroups = useMemo(() => {
    if (activeTab !== 'organisations') return [];
    const groups = organisations.map(org => {
      const members = users.filter(u => u.organisation_id === org.id);
      return { org, members };
    }).filter(g => g.members.length > 0 || g.org.status === 'active');

    if (search) {
      const q = search.toLowerCase();
      return groups.filter(g =>
        g.org.name?.toLowerCase().includes(q) ||
        g.org.company_name?.toLowerCase().includes(q) ||
        g.members.some(m => m.email?.toLowerCase().includes(q) || `${m.first_name} ${m.last_name}`.toLowerCase().includes(q))
      );
    }
    return groups;
  }, [organisations, users, activeTab, search]);

  // ── Pending invites ──
  const pendingInvites = useMemo(() => {
    if (activeTab !== 'pending') return [];
    let list = invitations.filter(i => i.status === 'pending');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.email?.toLowerCase().includes(q));
    }
    return list;
  }, [invitations, activeTab, search]);

  // ── Handlers ──
  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filteredUsers.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredUsers.map(u => u.id)));
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await api.put(`/users/${userId}`, { status: newStatus });
      toast.success(`User ${newStatus}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
    setConfirmDialog(null);
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch { toast.error('Failed to delete user'); }
    setConfirmDialog(null);
  };

  const [extraTimeUser, setExtraTimeUser] = useState(null);
  const [roleUser, setRoleUser] = useState(null);
  const handleAction = (type, user) => {
    if (type === 'extra_time') return setExtraTimeUser(user);
    if (type === 'change_role') return setRoleUser(user);
    return setConfirmDialog({ type, user });
  };

  const handleRename = async (u, full) => {
    const [first, ...rest] = full.split(/\s+/);
    if (!first) { toast.error('Name cannot be empty'); throw new Error('empty'); }
    const patch = { first_name: first, last_name: rest.join(' ') || u.last_name || '' };
    try {
      await api.put(`/users/${u.id}`, patch);
      setUsers(prev => prev.map(x => (x.id === u.id ? { ...x, ...patch } : x)));
      toast.success('Name updated');
    } catch (e) { toast.error('Could not update name'); throw e; }
  };

  // ── Bulk actions ──
  const handleBulkAction = async (action, value) => {
    const ids = [...selectedIds];
    if (ids.length === 0) { toast.error('Select at least one user'); return; }

    if (action === 'activate' || action === 'role') {
      const label = action === 'activate' ? 'Reactivate' : `Change role to ${ROLE_MAP[value]?.label}`;
      if (!window.confirm(`${label} for ${ids.length} selected users?`)) return;
      let done = 0;
      for (const id of ids) {
        try {
          if (action === 'activate') await api.put(`/users/${id}`, { status: 'active' });
          else await api.put(`/users/${id}/role`, { role: value });
          done++;
        } catch { /* continue */ }
      }
      toast.success(`Updated ${done} of ${ids.length} users`);
      setSelectedIds(new Set());
      fetchData();
      return;
    }

    if (action === 'export') {
      const selected = users.filter(u => selectedIds.has(u.id));
      const csv = ['Name,Email,Role,Status,Organisation,Joined']
        .concat(selected.map(u => `"${u.first_name} ${u.last_name}","${u.email}","${u.role}","${u.status}","${orgNameById(u.organisation_id)}","${u.created_at?.slice(0, 10)}"`))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'selected_users.csv'; a.click();
      toast.success(`Exported ${ids.length} users`);
      return;
    }

    if (action === 'suspend' || action === 'delete') {
      const label = action === 'suspend' ? 'Suspend' : 'Delete';
      if (!window.confirm(`${label} ${ids.length} selected users?`)) return;
      let done = 0;
      for (const id of ids) {
        try {
          if (action === 'suspend') await api.put(`/users/${id}`, { status: 'suspended' });
          else await api.delete(`/users/${id}`);
          done++;
        } catch { /* continue */ }
      }
      toast.success(`${label}ed ${done} users`);
      setSelectedIds(new Set());
      fetchData();
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/users/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'users_export.csv'; document.body.appendChild(a); a.click(); a.remove();
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const createUser = async () => {
    if (!addUserForm.email.trim()) { toast.error('Email required'); return; }
    if (!addUserForm.first_name.trim()) { toast.error('First name required'); return; }
    setAddUserSaving(true);
    try {
      const payload = {
        email: addUserForm.email, first_name: addUserForm.first_name, last_name: addUserForm.last_name,
        role: addUserForm.role, organisation_id: addUserForm.organisation_id || null, status: addUserForm.status,
      };
      if (!addUserForm.autoPassword && addUserForm.password) payload.password = addUserForm.password;
      const res = await api.post('/users/admin-create', payload);
      const genPass = res.data.generated_password;
      if (genPass) { setCreatedPassword(genPass); toast.success(`User created`); }
      else { toast.success('User created'); setShowAddUser(false); }
      fetchData();
      setAddUserForm({ email: '', first_name: '', last_name: '', password: '', role: 'learner', organisation_id: '', status: 'active', autoPassword: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddUserSaving(false); }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/users/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.created} users`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} rows had errors`);
      fetchData(); setShowImport(false);
    } catch { toast.error('Import failed'); }
  };

  // ── Stats ──
  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter(u => u.role === 'super_admin' || u.role === 'content_manager').length,
    orgUsers: users.filter(u => !!u.organisation_id).length,
    individuals: users.filter(u => !u.organisation_id && !u.is_resident).length,
    residents: users.filter(u => u.is_resident).length,
    pending: invitations.filter(i => i.status === 'pending').length,
  }), [users, invitations]);

  const toggleOrg = (id) => setExpandedOrgs(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Render ──
  const tableHeaders = (
    <tr>
      <th className="px-4 py-3 w-10">
        {activeTab !== 'organisations' && activeTab !== 'pending' && (
          <Checkbox checked={selectedIds.size > 0 && selectedIds.size === filteredUsers.length} onCheckedChange={selectAll} data-testid="select-all" />
        )}
      </th>
      <SortHeader label="User" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
      <SortHeader label="Email" field="email" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
      {activeTab === 'organisations' ? (
        <>
          <th className="px-4 py-3 text-left font-medium text-slate-600 hidden sm:table-cell">Plan</th>
          <th className="px-4 py-3 text-left font-medium text-slate-600 hidden sm:table-cell">Status</th>
        </>
      ) : (
        <>
          <th className="px-4 py-3 text-left font-medium text-slate-600 hidden sm:table-cell">Role</th>
          <th className="px-4 py-3 text-left font-medium text-slate-600 hidden sm:table-cell">Status</th>
        </>
      )}
      <SortHeader label="Joined" field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
      <th className="px-4 py-3 text-right font-medium text-slate-600 w-16">Actions</th>
    </tr>
  );

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-5" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">User Management</h1>
            <p className="text-slate-500 mt-0.5 text-sm">{stats.total} users across your platform</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-users-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Import
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> Export
            </Button>
            <Button size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white" onClick={() => { setShowAddUser(true); setCreatedPassword(null); }} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add User
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#2a3e5d]', bg: 'bg-slate-50' },
            { label: 'Admins', value: stats.admins, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'In Orgs', value: stats.orgUsers, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Individual', value: stats.individuals, color: 'text-teal-600', bg: 'bg-teal-50' },
            { label: 'Residents', value: stats.residents, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Pending', value: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-3 text-center`}>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto" data-testid="user-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const count = tab.id === 'all' ? stats.total
              : tab.id === 'organisations' ? organisations.length
              : tab.id === 'individuals' ? stats.individuals
              : tab.id === 'residents' ? stats.residents
              : stats.pending;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedIds(new Set()); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-[#2a3e5d] shadow-sm'
                    : 'text-slate-500 hover:text-[#2a3e5d]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="text-[10px] bg-slate-200/80 text-slate-600 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search name, email, or organisation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-9"
              data-testid="user-search-input"
            />
          </div>
          {activeTab !== 'organisations' && activeTab !== 'pending' && (
            <>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-36 h-9" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="content_manager">Content Mgr</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                  <SelectItem value="learner">Learner</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              {activeTab === 'all' && (
                <Select value={orgFilter} onValueChange={setOrgFilter}>
                  <SelectTrigger className="w-44 h-9" data-testid="filter-org"><SelectValue placeholder="Organisation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organisations</SelectItem>
                    {organisations.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.name || o.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-36 h-9"><SelectValue placeholder="Date" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-[#20acf6]/5 border border-[#20acf6]/20 rounded-xl" data-testid="bulk-actions">
            <span className="text-sm font-medium text-[#2a3e5d]">{selectedIds.size} selected</span>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('export')} data-testid="bulk-export">
                <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
              </Button>
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleBulkAction('suspend')} data-testid="bulk-suspend">
                <Ban className="h-3.5 w-3.5 mr-1" /> Suspend
              </Button>
              <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => handleBulkAction('activate')} data-testid="bulk-activate">
                <UserCheck className="h-3.5 w-3.5 mr-1" /> Reactivate
              </Button>
              <Select value="" onValueChange={(v) => handleBulkAction('role', v)}>
                <SelectTrigger className="h-9 w-40" data-testid="bulk-role-select"><SelectValue placeholder="Change role…" /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_MAP).map(([k, r]) => <SelectItem key={k} value={k}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleBulkAction('delete')} data-testid="bulk-delete">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            </div>
          </div>
        )}

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : activeTab === 'pending' ? (
              /* Pending Invites */
              pendingInvites.length === 0 ? (
                <div className="text-center py-16 text-slate-400">No pending invitations</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Role</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Invited</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Expires</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingInvites.map(inv => (
                        <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-3 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-700">{inv.email}</span>
                          </td>
                          <td className="px-4 py-3"><RoleBadge role={inv.team_role || 'learner'} /></td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{inv.created_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{inv.expires_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3"><StatusBadge status="pending" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : activeTab === 'organisations' ? (
              /* Organisations grouped */
              orgGroups.length === 0 ? (
                <div className="text-center py-16 text-slate-400">No organisations found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">{tableHeaders}</thead>
                    <tbody>
                      {orgGroups.map(({ org, members }) => (
                        <OrgRow
                          key={org.id}
                          org={org}
                          members={members}
                          expanded={expandedOrgs.has(org.id)}
                          onToggle={() => toggleOrg(org.id)}
                          selectedIds={selectedIds}
                          onSelect={toggleSelect}
                          onAction={handleAction}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* Regular user list */
              filteredUsers.length === 0 ? (
                <div className="text-center py-16 text-slate-400">No users found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">{tableHeaders}</thead>
                    <tbody>
                      {filteredUsers.map(u => (
                        <UserRow key={u.id} u={u} selected={selectedIds.has(u.id)} onSelect={toggleSelect} onAction={handleAction} onRename={handleRename} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center">
          Showing {activeTab === 'pending' ? pendingInvites.length : activeTab === 'organisations' ? `${orgGroups.length} organisations` : `${filteredUsers.length} users`}
        </p>
      </div>

      {/* ── Confirm Dialog ── */}
      <ChangeRoleDialog user={roleUser} open={!!roleUser} onOpenChange={(o) => !o && setRoleUser(null)}
        onChanged={(r) => setUsers(prev => prev.map(x => x.id === r.user_id ? { ...x, role: r.role, organisation_id: r.organisation_id } : x))} />
      <ExtraTimeDialog user={extraTimeUser} open={!!extraTimeUser} onOpenChange={(o) => !o && setExtraTimeUser(null)} />
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.type === 'delete' ? 'Delete User' : confirmDialog?.type === 'suspend' ? 'Suspend User' : 'Activate User'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to {confirmDialog?.type}{' '}
            <strong>{confirmDialog?.user?.first_name} {confirmDialog?.user?.last_name}</strong> ({confirmDialog?.user?.email})?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            {confirmDialog?.type === 'delete' ? (
              <Button variant="destructive" onClick={() => handleDelete(confirmDialog.user.id)} data-testid="confirm-action-btn">Delete</Button>
            ) : (
              <Button className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                onClick={() => handleStatusChange(confirmDialog.user.id, confirmDialog.type === 'suspend' ? 'suspended' : 'active')}
                data-testid="confirm-action-btn"
              >Confirm</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add User Dialog ── */}
      <Dialog open={showAddUser} onOpenChange={v => { setShowAddUser(v); if (!v) setCreatedPassword(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={addUserForm.first_name} onChange={e => setAddUserForm(p => ({ ...p, first_name: e.target.value }))} data-testid="new-user-first-name" /></div>
              <div><Label>Last Name</Label><Input value={addUserForm.last_name} onChange={e => setAddUserForm(p => ({ ...p, last_name: e.target.value }))} data-testid="new-user-last-name" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={addUserForm.email} onChange={e => setAddUserForm(p => ({ ...p, email: e.target.value }))} data-testid="new-user-email" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={addUserForm.role} onValueChange={v => setAddUserForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="org_admin">Org Admin</SelectItem>
                    <SelectItem value="content_manager">Content Manager</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organisation</Label>
                <Select value={addUserForm.organisation_id || 'none'} onValueChange={v => setAddUserForm(p => ({ ...p, organisation_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Organisation</SelectItem>
                    {organisations.map(o => <SelectItem key={o.id} value={o.id}>{o.name || o.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Switch checked={addUserForm.autoPassword} onCheckedChange={v => setAddUserForm(p => ({ ...p, autoPassword: v }))} />
              <div>
                <p className="text-sm font-medium text-slate-800">Auto-generate password</p>
                <p className="text-xs text-slate-500">System creates a secure random password</p>
              </div>
            </div>
            {!addUserForm.autoPassword && (
              <div><Label>Password</Label><Input type="text" value={addUserForm.password} onChange={e => setAddUserForm(p => ({ ...p, password: e.target.value }))} placeholder="Enter password" data-testid="new-user-password" /></div>
            )}
            {createdPassword && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Generated Password</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm bg-white px-2 py-1 rounded border border-green-200 flex-1">{createdPassword}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(createdPassword); toast.success('Copied'); }}><Copy className="h-3 w-3" /></Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={createUser} disabled={addUserSaving} data-testid="create-user-btn">
              {addUserSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CSV Import Dialog ── */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent data-testid="import-users-dialog">
          <DialogHeader><DialogTitle>Import Users from CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload a CSV with: <code className="bg-slate-100 px-1 rounded text-xs">email, first_name, last_name, role, organisation_id, password</code></p>
            <Input type="file" accept=".csv" onChange={handleCsvImport} data-testid="csv-import-input" />
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
